import {
  REPLAY_FORMAT_VERSION,
  RULES_VERSION,
  isActionShape,
  isReplayShape,
  replayFormatOf,
  type Action,
  type MatchResult,
  type Replay,
} from '../engine';
import type { Controller, MatchSetup } from './matchHost';
import { readJson, removeKey, writeJson } from './storage';

/** Saved duel in progress (§16.5: a match in progress SHOULD be resumable). */
export interface DuelSave {
  readonly version: 1;
  readonly setup: MatchSetup;
  readonly actions: readonly Action[];
  readonly savedAt: string;
}

const DUEL_KEY = 'duel.v1';
const REPLAYS_KEY = 'replays.v1';
const MAX_REPLAYS = 30;

function isController(v: unknown): v is Controller {
  if (typeof v !== 'object' || v === null) return false;
  const c = v as { kind?: unknown; name?: unknown; difficulty?: unknown };
  if (typeof c.name !== 'string') return false;
  if (c.kind === 'human') return true;
  return (
    c.kind === 'ai' &&
    (c.difficulty === 'easy' || c.difficulty === 'standard' || c.difficulty === 'hard')
  );
}

function validateDuel(v: unknown): DuelSave | null {
  if (typeof v !== 'object' || v === null) return null;
  const d = v as Partial<DuelSave>;
  if (d.version !== 1 || !d.setup || !Array.isArray(d.actions)) return null;
  if (typeof d.setup.seed !== 'string' || typeof d.setup.config !== 'object') return null;
  if (!d.actions.every(isActionShape)) return null;
  const controllers = d.setup.controllers as Record<string, unknown> | undefined;
  if (!controllers || !Object.values(controllers).every(isController)) return null;
  return d as DuelSave;
}

export const loadDuel = (): DuelSave | null => readJson(DUEL_KEY, validateDuel);

export const saveDuel = (setup: MatchSetup, actions: readonly Action[]): string | null =>
  writeJson(DUEL_KEY, {
    version: 1,
    setup,
    actions,
    savedAt: new Date().toISOString(),
  } satisfies DuelSave);

export const clearDuel = (): void => removeKey(DUEL_KEY);

/** A replay in the library, with a short description. */
export interface SavedReplay {
  readonly id: string;
  readonly savedAt: string;
  readonly title: string;
  readonly result: MatchResult | null;
  readonly names: Readonly<Record<'S' | 'N', string>>;
  readonly replay: Replay;
}

function validateReplays(v: unknown): SavedReplay[] | null {
  if (!Array.isArray(v)) return null;
  return v.filter(
    (r): r is SavedReplay =>
      typeof r === 'object' &&
      r !== null &&
      typeof (r as SavedReplay).id === 'string' &&
      isReplayShape((r as SavedReplay).replay),
  );
}

export const loadReplays = (): SavedReplay[] => readJson(REPLAYS_KEY, validateReplays) ?? [];

export function addReplay(entry: Omit<SavedReplay, 'id' | 'savedAt'>): {
  error: string | null;
  list: SavedReplay[];
} {
  const now = new Date();
  const item: SavedReplay = {
    ...entry,
    id: `${now.getTime().toString(36)}-${loadReplays().length}`,
    savedAt: now.toISOString(),
  };
  const list = [item, ...loadReplays()].slice(0, MAX_REPLAYS);
  return { error: writeJson(REPLAYS_KEY, list), list };
}

export function deleteReplay(id: string): SavedReplay[] {
  const list = loadReplays().filter((r) => r.id !== id);
  writeJson(REPLAYS_KEY, list);
  return list;
}

/** Parses an imported replay file; rejects other rules versions and replay formats (§16.5). */
export function parseReplayFile(text: string): Replay | null {
  try {
    const data: unknown = JSON.parse(text);
    const replay = isReplayShape(data)
      ? data
      : isReplayShape((data as { replay?: unknown })?.replay)
        ? (data as { replay: Replay }).replay
        : null;
    if (!replay || replay.rulesVersion !== RULES_VERSION) return null;
    if (replayFormatOf(replay) !== REPLAY_FORMAT_VERSION) return null;
    if (!replay.actions.every(isActionShape)) return null;
    return replay;
  } catch {
    return null;
  }
}

/** Offers a JSON file for download. */
export function downloadJson(filename: string, data: unknown): void {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
